import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';

const FaceGuideOverlay = () => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '60%',
      height: '70%',
      border: '2px dashed rgba(255, 255, 255, 0.7)',
      borderRadius: '50%',
      pointerEvents: 'none',
      zIndex: 2,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        height: '90%',
        border: '2px dashed rgba(255, 255, 255, 0.3)',
        borderRadius: '50%',
      }
    }}
  />
);


function ProfilePicturePopup({ onClose, setImage }) {
  const [selectedCamera, setSelectedCamera] = useState('Integrated Camera');
  const [imageSrc, setImageSrc] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraLoading, setCameraLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  useEffect(() => {
    let isMounted = true;
    setCameraLoading(true);

    async function initializeCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (!isMounted) return;
        streamRef.current = stream;

        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isMounted) return;
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        setAvailableCameras(videoDevices);

        if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
          await startVideo(videoDevices[0].deviceId);
        }
        setCameraLoading(false);
      } catch (err) {
        console.error('Error accessing camera:', err);
        setCameraLoading(false);
      }
    }

    initializeCamera();

    return () => {
      isMounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startVideo = async (camera) => {
    let retries = 0;
    const maxRetries = 5;

    const tryStartVideo = async () => {
      try {
        if (!videoRef.current) {
          if (retries < maxRetries) {
            retries++;
            console.log(`Video element not ready, retrying (${retries}/${maxRetries})...`);
            setTimeout(tryStartVideo, 200);
            return;
          }
          throw new Error('Video element not available after retries');
        }

        if (streamRef?.current) {
          streamRef?.current.getTracks().forEach((track) => track.stop());
        }

        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: camera } });
        if (videoRef.current.srcObject !== stream) {
          if (videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
          }

          videoRef.current.srcObject = stream;
          streamRef.current = stream;

          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch((err) => console.error('Error playing video: ', err));
          };
        }
      } catch (err) {
        console.error('Error accessing camera: ', err);
        setCameraLoading(false);
      }
    };

    tryStartVideo();
  };

  const resetPicture = () => {
    setImageSrc(null);
    changeCamera(selectedCamera);
  };

  const takePicture = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL('image/jpeg');
    setImageSrc(imageData);
  };

  const changeCamera = (deviceId) => {
    startVideo(deviceId);
    setSelectedCamera(deviceId);
  };

  const onSave = () => {
    setImage(imageSrc);
    onClose();
  };

  const chooseFromFiles = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };


  return (
    <Dialog 
      open={true} 
      onClose={onClose} 
      fullWidth 
      fullScreen={isMobile}
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' }
        }
      }}
    >
      <DialogTitle
        sx={{
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          pb: { xs: 1, sm: 2 }
        }}
      >
        Add Profile Picture
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: { xs: 4, sm: 8 }, 
            top: { xs: 4, sm: 8 }, 
            color: 'grey[500]' 
          }}
        >
          <Icon icon="fa6-solid:close" width={isMobile ? "24" : "28"} height={isMobile ? "24" : "28"} color="#70758a" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 2 }
        }}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            mb: { xs: 1, sm: 2 }
          }}
        >
          Please take a profile image for our system with your device or from your files.
        </Typography>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              sx={{ mt: { xs: 1, sm: 2 }, position: 'relative' }}
            >
              <Box 
                sx={{ 
                  position: 'relative', 
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 484 }, 
                  height: { xs: 'auto', sm: 408 },
                  aspectRatio: { xs: '4/3', sm: 'auto' },
                  mb: 2, 
                  border: '1px solid lightgrey', 
                  bgcolor: '#000',
                  overflow: 'hidden'
                }}
              >
                {!imageSrc && !cameraLoading && (
                  <>
                    <video
                      ref={videoRef}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'scaleX(-1)',
                        backgroundColor: 'black'
                      }}
                      autoPlay
                      playsInline
                      muted
                    />
                    <FaceGuideOverlay />
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: { xs: 12, sm: 16 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: 'white',
                        textAlign: 'center',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        padding: { xs: '6px 8px', sm: '4px 8px' },
                        borderRadius: '4px',
                        width: { xs: '90%', sm: '80%' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Face must be clearly visible, no hats or glasses, clean background
                    </Typography>
                  </>
                )}
                {imageSrc && (
                  <img
                    src={imageSrc}
                    alt="Captured"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </Box>

              {!!imageSrc && (
                <IconButton
                  onClick={resetPicture}
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '20px', sm: '40px' },
                    background: '#fff',
                    padding: { xs: '12px', sm: '15px' },
                    '&:hover': {
                      backgroundColor: '#666666b5',
                    },
                  }}
                >
                  <Icon icon="fa6-solid:camera-rotate" width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} color="#00A3FF" />
                </IconButton>
              )}
              {!!!imageSrc && (
                <IconButton
                  onClick={takePicture}
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '20px', sm: '40px' },
                    background: '#fff',
                    padding: { xs: '12px', sm: '15px' },
                    '&:hover': {
                      backgroundColor: '#666666b5',
                    },
                  }}
                >
                  <Icon icon="fa6-solid:camera" width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} color="#00A3FF" />
                </IconButton>
              )}
            </Grid>
            <Grid container justifyContent="center">
              <Grid 
                item 
                xs={12} 
                sm={8}
                sx={{ 
                  px: { xs: 0, sm: 2 }
                }}
              >
                <Select
                  value={selectedCamera}
                  fullWidth
                  onChange={(e) => changeCamera(e.target.value)}
                  sx={{
                    borderRadius: { xs: 2, sm: 10 },
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                  startAdornment={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Icon icon="fa6-solid:camera" width={isMobile ? "20" : "28"} height={isMobile ? "20" : "28"} color="#70758a" />
                    </Box>
                  }
                  variant="outlined"
                >
                  {availableCameras.map((x) => (
                    <MenuItem 
                      key={`camera-${x.deviceId}`} 
                      value={x.deviceId}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {x.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>
      <DialogActions 
        sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 2 },
          pt: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}
      >
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={onClose}
          fullWidth={isMobile}
          sx={{
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            py: { xs: 1, sm: 0.75 }
          }}
        >
          CANCEL
        </Button>

        {!!imageSrc && (
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            fullWidth={isMobile}
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            CONTINUE
          </Button>
        )}

        {!!!imageSrc && (
          <Button
            variant="contained"
            color="primary"
            component="label"
            fullWidth={isMobile}
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            CHOOSE FROM FILES
            <input type="file" hidden accept=".jpg, .jpeg, .png" onChange={chooseFromFiles} />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ProfilePicturePopup;
